import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const TABS = ["all", "paid", "sent_to_ton_atelier", "in_production", "shipped", "delivered", "cancelled", "awaiting_payment"];

export default function AdminOrders() {
  const { t, money, lang } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/orders", { params: { status: tab } }).then((r) => setData(r.data)).catch(() => setData({ orders: [], counts: {} }));
  }, [tab]);

  const count = (s) => (s === "all" ? Object.values(data?.counts || {}).reduce((a, b) => a + b, 0) : data?.counts?.[s] || 0);

  return (
    <div data-testid="admin-orders-page">
      <h1 className="font-display text-3xl sm:text-4xl">{t("admin.orders")}</h1>
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        {TABS.map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`orders-tab-${s}`}
            className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-bold transition-colors ${tab === s ? "border-gold bg-gold text-ink" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
            {t(`admin_status.${s}`)} <span className="ml-1 font-mono-num opacity-70">{data ? count(s) : ""}</span>
          </button>
        ))}
      </div>
      <div className="panel mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
            <tr>{["order", "date", "customer", "items", "total", "profit", "status"].map((h) => <th key={h} className="px-4 py-3 font-extrabold">{t(`admin.${h}`)}</th>)}</tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">{t("common.loading")}</td></tr>}
            {data?.orders.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="orders-empty">{t("admin.no_orders")}</td></tr>}
            {data?.orders.map((o) => (
              <tr key={o.id} onClick={() => nav(`/admin/orders/${o.id}`)} className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/5" data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-mono-num font-bold text-gold">{o.order_number}</td>
                <td className="px-4 py-3 text-slate-400">{new Date(o.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="px-4 py-3"><p className="font-bold">{o.customer.business || o.customer.name}</p><p className="text-xs text-slate-500">{o.shipping?.city}, {o.shipping?.province}</p></td>
                <td className="px-4 py-3">{o.items.map((i) => (i.print_type === "dtf" ? "DTF" : "UV")).join(" · ")}</td>
                <td className="px-4 py-3 font-mono-num">{money(o.total)}</td>
                <td className="px-4 py-3 font-mono-num text-emerald-400">{money(o.profit_total)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
